import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonContent, IonItem, IonLabel, IonModal, IonPage } from "@ionic/react";
import { Helmet } from "react-helmet";

const words = [
    "TRIANGLE", "SQUARE", "CIRCLE", "RECTANGLE", "PENTAGON",
    "HEXAGON", "OCTAGON", "DIAMOND", "STAR", "CUBE",

    // Colors
    "RED", "BLUE", "GREEN", "YELLOW", "ORANGE",
    "PURPLE", "PINK", "BROWN", "BLACK", "WHITE",
    "GRAY", "CYAN", "MAGENTA", "GOLD", "SILVER",

    // Animals
    "LION", "TIGER", "ELEPHANT", "GIRAFFE", "ZEBRA",
    "MONKEY", "KANGAROO", "PANDA", "BEAR", "FOX",
    "WOLF", "RABBIT", "DEER", "HORSE", "DOLPHIN",
    "SHARK", "OCTOPUS", "EAGLE", "OWL", "PARROT",

    // Fruits
    "APPLE", "BANANA", "ORANGE", "STRAWBERRY", "PINEAPPLE",
    "GRAPES", "WATERMELON", "CHERRY", "MANGO", "PEACH",
    "PEAR", "PLUM", "COCONUT", "KIWI", "LEMON",

    // Vegetables
    "CARROT", "TOMATO", "POTATO", "CUCUMBER", "ONION",
    "GARLIC", "BROCCOLI", "SPINACH", "CABBAGE", "PEPPER",
    "MUSHROOM", "CORN", "PUMPKIN", "RADISH", "BEETROOT",

    // Vehicles
    "CAR", "TRUCK", "BUS", "BICYCLE", "MOTORCYCLE",
    "TRAIN", "PLANE", "BOAT", "HELICOPTER", "SUBMARINE",

    // Nature
    "SUN", "MOON", "RAINBOW", "CLOUD", "STORM",
    "SNOWFLAKE", "LIGHTNING", "MOUNTAIN", "RIVER", "OCEAN",
    "FOREST", "DESERT", "VOLCANO", "WATERFALL", "ISLAND",

    // Body Parts
    "EYE", "NOSE", "MOUTH", "EAR", "HAND",
    "FOOT", "HEAD", "ARM", "LEG", "HEART",

    // Household Items
    "CHAIR", "TABLE", "BED", "SOFA", "LAMP",
    "MIRROR", "CLOCK", "DOOR", "WINDOW", "BOOK",

    // Actions
    "JUMP", "RUN", "WALK", "CLIMB", "DANCE",
    "SING", "WRITE", "DRAW", "READ", "LAUGH",

    // Sports
    "SOCCER", "BASKETBALL", "TENNIS", "BASEBALL", "CRICKET",
    "SWIMMING", "RUNNING", "BOXING", "CYCLING", "WRESTLING",

    // Opposites
    "HOT", "COLD", "BIG", "SMALL", "TALL",
    "SHORT", "FAST", "SLOW", "HARD", "SOFT",

    // Days of the Week
    "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY",
    "SATURDAY", "SUNDAY",

    // Numbers
    "ONE", "TWO", "THREE", "FOUR", "FIVE",
    "SIX", "SEVEN", "EIGHT", "NINE", "TEN"
];


const positions = [
    { number: 1, text: "1st", word: "First" },
    { number: 2, text: "2nd", word: "Second" },
    { number: 3, text: "3rd", word: "Third" },
    { number: 4, text: "4th", word: "Fourth" },
    { number: 5, text: "5th", word: "Fifth" },
    { number: 6, text: "6th", word: "Sixth" },
    { number: 7, text: "7th", word: "Seventh" },
    { number: 8, text: "8th", word: "Eighth" },
];

const IdentifyLetter: React.FC = () => {
    const [selectedWord, setSelectedWord] = useState<string>("");
    const [question, setQuestion] = useState<string>("");
    const [correctAnswer, setCorrectAnswer] = useState<string>("");
    const [options, setOptions] = useState<string[]>([]);
    const [message, setMessage] = useState<string>("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateNewRound();
        document.title = "Ordinal Number Riddle Worksheets for UKG | Fun Learning Activities";
    }, []);

    const generateNewRound = () => {
        // Select a random word
        const newWord = words[Math.floor(Math.random() * words.length)];

        // Select a valid position within the word length
        const validPositions = positions.filter(pos => pos.number <= newWord.length);
        const posIndex = Math.floor(Math.random() * validPositions.length);
        const posData = validPositions[posIndex];

        // Get the correct letter at that position (0-based index)
        const correctLetter = newWord[posData.number - 1];

        // Generate 3 incorrect options from the word (excluding correct letter)
        const randomOptions = newWord
            .split("")
            .filter(letter => letter !== correctLetter) // Remove correct letter
            .sort(() => 0.5 - Math.random()) // Shuffle
            .slice(0, 3); // Pick 3 random wrong answers

        // Insert the correct answer at a **random** position
        const randomIndex = Math.floor(Math.random() * 4); // Random position between 0-3
        randomOptions.splice(randomIndex, 0, correctLetter);

        setSelectedWord(newWord);
        setQuestion(`The ${posData.word} letter in the word ${newWord} is?`);
        setCorrectAnswer(correctLetter);
        setOptions(randomOptions);
        setMessage("");
    };

    const checkAnswer = (selected: string) => {
        if (selected === correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                generateNewRound();
                setShowImage(false);
                setMessage("");
            }, 5000);
        } else {
            setShowImage(false);
            setMessage("❌ Oops! Try again.");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Solve fun ordinal number riddles with your UKG child! Boost logical thinking and position awareness using 1st, 2nd, 3rd in our printable worksheets and games." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/ordinal-numbers/solve-riddle" />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart additionWord ordinalNumber matchImage match-container game-container" style={{ padding: "20%" }}>
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Ordinal Number Riddles</h1>

                                <p>
                                    Learning ordinal numbers doesn&apos;t have to be boring! With our <strong>Solve the Riddle</strong> worksheets, UKG students explore positions like <strong>1st, 2nd, 3rd</strong>, while solving fun and logical clues. It&apos;s the perfect way to build <strong>critical thinking</strong> and <strong>math vocabulary</strong> at the same time.
                                </p>

                                <h2>🎯 What Your Child Will Learn</h2>
                                <ul>
                                    <li>Understand and use ordinal numbers up to 10th</li>
                                    <li>Develop listening and comprehension skills through riddle-solving</li>
                                    <li>Improve sequencing and logical reasoning</li>
                                    <li>Follow instructions based on position (first, third, last, etc.)</li>
                                </ul>

                                <h2>🧠 Example Riddles for UKG Kids</h2>
                                <ul>
                                    <li>“I come right after the 2nd. Who am I?” → <strong>3rd</strong></li>
                                    <li>“I am between the 1st and 3rd object. What’s my position?” → <strong>2nd</strong></li>
                                    <li>“The apple is in the 5th place. What fruit is in the 3rd?”</li>
                                </ul>
                                <p>These activities keep children engaged while building foundational math skills.</p>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/smallest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Smallest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/biggest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Biggest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/number-comparison"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Number Comparison</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🎲 Activities You Can Try at Home</h2>
                                <ul>
                                    <li>Line up toys and create riddles like: “Which toy is 4th from the left?”</li>
                                    <li>Story-based riddles: “The cat sat in the 2nd chair. Who sat first?”</li>
                                    <li>Object hunt using position-based hints (“Find the 1st spoon in the kitchen”)</li>
                                </ul>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Ordinal Number Riddles</h1>

                            <p>
                                Learning ordinal numbers doesn&apos;t have to be boring! With our <strong>Solve the Riddle</strong> worksheets, UKG students explore positions like <strong>1st, 2nd, 3rd</strong>, while solving fun and logical clues. It&apos;s the perfect way to build <strong>critical thinking</strong> and <strong>math vocabulary</strong> at the same time.
                            </p>

                            <h2>🎯 What Your Child Will Learn</h2>
                            <ul>
                                <li>Understand and use ordinal numbers up to 10th</li>
                                <li>Develop listening and comprehension skills through riddle-solving</li>
                                <li>Improve sequencing and logical reasoning</li>
                                <li>Follow instructions based on position (first, third, last, etc.)</li>
                            </ul>

                            <h2>🧠 Example Riddles for UKG Kids</h2>
                            <ul>
                                <li>“I come right after the 2nd. Who am I?” → <strong>3rd</strong></li>
                                <li>“I am between the 1st and 3rd object. What’s my position?” → <strong>2nd</strong></li>
                                <li>“The apple is in the 5th place. What fruit is in the 3rd?”</li>
                            </ul>
                            <p>These activities keep children engaged while building foundational math skills.</p>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/mathematics/smallest-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Smallest Number</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/mathematics/biggest-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Biggest Number</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/mathematics/number-comparison"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Number Comparison</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🎲 Activities You Can Try at Home</h2>
                            <ul>
                                <li>Line up toys and create riddles like: “Which toy is 4th from the left?”</li>
                                <li>Story-based riddles: “The cat sat in the 2nd chair. Who sat first?”</li>
                                <li>Object hunt using position-based hints (“Find the 1st spoon in the kitchen”)</li>
                            </ul>
                        </article>
                        <h3>{question}</h3>
                        <div className="optionImageDiv" style={{ display: "flex", justifyContent: "center", flexWrap: "wrap" }}>
                            {options.map((letter, index) => (
                                <div
                                    className="background-blue"
                                    key={index}
                                    onClick={() => checkAnswer(letter)}
                                    style={{ margin: "5px", padding: "10px", width: "60px" }}
                                >
                                    {letter}
                                </div>
                            ))}
                        </div>
                        <p style={{ fontSize: "20px", marginTop: "20px" }}>{message}</p>
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img style={{ height: "150px" }} className="img-fluid" src={process.env.REACT_APP_WELDONE_LINK} />
                    </div>
                </>
            )}
        </>
    );
};

export default IdentifyLetter;
